import { useMemo } from 'react';
import { CalendarInputCommonProps, CalendarPresetItem } from '../types';
import { useProviderConfig } from '../../../provider';
import { sub, subMilliseconds } from 'date-fns';
import {
  isSelectionToAvailable,
  isCursorItemInterval,
  isInstantWithinItem,
  applyFromWithinInterval,
  applyToWithinInterval,
  isCursorItemComplete,
  isItemValid,
  getNextFocus,
  getNewCursorItem,
} from '../utils';
import { constraintTrue } from '../constraints';

export interface UsePresetListParams extends CalendarInputCommonProps {
  presets?: CalendarPresetItem[];
  shouldConfirm?: boolean;
}

export interface UsePresetListItem extends CalendarPresetItem {
  disabled: boolean;
  selected: boolean;
  onClick: () => void;
}

export function usePresetList(params: UsePresetListParams): UsePresetListItem[] {
  const { locale } = useProviderConfig();
  const defaultPresets = useMemo((): CalendarPresetItem[] => {
    const today = new Date();
    const labels = locale?.DatePickerRange.dataPreset;
    return [
      { label: labels?.today ?? '', interval: { from: today, to: today } },
      { label: labels?.['1-week'] ?? '', interval: { from: sub(today, { days: 6 }), to: today } },
      { label: labels?.['1-month'] ?? '', interval: { from: sub(today, { months: 1, days: 1 }), to: today } },
      { label: labels?.['3-month'] ?? '', interval: { from: sub(today, { months: 3, days: 1 }), to: today } },
      { label: labels?.['6-month'] ?? '', interval: { from: sub(today, { months: 6, days: 1 }), to: today } },
      { label: labels?.['1-year'] ?? '', interval: { from: sub(today, { months: 12, days: 1 }), to: today } },
      { label: labels?.all ?? '', interval: { from: new Date('2000-01-01'), to: today } },
    ];
  }, [locale]);
  const presets = params.presets ?? defaultPresets;
  const {
    constraint,
    resolution,
    items,
    cursor,
    target,
    onCursorChange,
    onConfirm,
    shouldConfirm,
    tolerateInvalidValues,
  } = params;
  const result = useMemo(() => {
    const activeConstraint = tolerateInvalidValues ? constraintTrue() : constraint;
    return presets.map((preset): UsePresetListItem => {
      const { interval } = preset;
      const fromInterval = resolution.resolve(interval.from);
      const toInterval = resolution.resolve(interval.to);

      // from/to가 동시에 설정될 수 있음을 확인하기 위해 from을 실제로 적용한 값에 to가 그대로 대입될 수 있는지 확인합니다.
      let fromSet = null;
      try {
        fromSet = applyFromWithinInterval(activeConstraint, { items }, cursor?.item ?? {}, fromInterval, true);
      } catch (e) {
        //
      }

      return {
        ...preset,
        disabled: fromSet == null || !isSelectionToAvailable(activeConstraint, { items }, fromSet, toInterval),
        selected:
          isCursorItemInterval(cursor?.item) &&
          isInstantWithinItem(fromInterval, cursor.item.from) &&
          isInstantWithinItem(toInterval, subMilliseconds(cursor.item.to, 1)),
        onClick: () => {
          let nextItem = cursor?.item ?? getNewCursorItem();
          if (['from', 'all'].includes(target ?? 'all')) {
            nextItem = applyFromWithinInterval(activeConstraint, { items }, nextItem, fromInterval);
          }
          if (['to', 'all'].includes(target ?? 'all')) {
            nextItem = applyToWithinInterval(activeConstraint, { items }, nextItem, toInterval);
          }

          if (shouldConfirm) {
            if (isCursorItemComplete(nextItem) && isItemValid(activeConstraint, { items }, nextItem)) {
              onConfirm(nextItem);
            } else {
              onCursorChange({
                ...cursor,
                item: nextItem,
                focus: getNextFocus(cursor, target),
                toggled: true,
              });
            }
          } else {
            onCursorChange({ ...cursor, item: nextItem, focus: cursor?.focus });
          }
        },
      };
    });
  }, [
    constraint,
    cursor,
    items,
    onConfirm,
    onCursorChange,
    presets,
    resolution,
    shouldConfirm,
    target,
    tolerateInvalidValues,
  ]);
  return result;
}
